import React, { useEffect, useRef, useState } from "react";

/**
 * ClientConsoleVS — the "View Section": the bottom two thirds of the client
 * console, holding whichever tile is selected.
 *
 * ─────────────────────────────────────────────────────────────────
 * TWO FIXED SLOTS. A PANEL NEVER CHANGES POSITION.
 *
 * This is the whole design, and it is not an optimisation. The obvious build —
 * "render the leaving panel first, the current one second" — moves a panel from
 * the second child to the first child when it starts leaving. That is a
 * different position in the React tree, so React UNMOUNTS the live instance and
 * MOUNTS A NEW ONE in the leaving slot.
 *
 * The consequence is a panel that is supposed to be fading out instead being
 * born again: empty, then its lazy query re-fires, then a spinner, then its
 * content fills in — all while it is mid-exit. That read as a flicker where the
 * outgoing panel "got brighter and then carried on fading". It was only ever
 * visible on Emails because Emails is the only section with content so far; the
 * same bug was waiting for every other panel.
 *
 * So there are two slots at fixed positions, keyed by INDEX, and the keys move
 * between them. `front` says which slot is the live one. A panel mounts once,
 * animates in, later animates out, and is unmounted — it is never relocated,
 * so it never re-runs its effects mid-transition.
 * ─────────────────────────────────────────────────────────────────
 *
 * ENTER AND EXIT ARE BOTH KEYFRAME ANIMATIONS, never transitions. A transition
 * needs a previous computed style to run from, which a freshly mounted node
 * does not have — that is what forced the old two-paint `arming` dance, and
 * `arming` being shared between the two panels is what let a mid-flight
 * re-render strip the exit class off a panel that was halfway out. Animations
 * run on mount, so each panel gets its class once and keeps it.
 *
 * DIRECTION. Tiles are numbered across both rows, and the motion matches the
 * direction you travelled along them:
 *
 *   to a HIGHER tile → new enters from the RIGHT, old exits LEFT
 *   to a LOWER tile  → new enters from the LEFT,  old exits RIGHT
 */

// Must match the animation durations on .scc-vs-panel in singleClientConsole.css.
const SLIDE_MS = 280;

const ClientConsoleVS = ({
  /** key of the selected tile */
  activeKey,
  /** every key in tile order — the VS derives direction from position */
  order,
  /** (key) => node. Called for the live panel and, mid-transition, the outgoing one */
  renderPanel,
}) => {
  /*
   * `order` is read through a ref and deliberately NOT a dependency.
   *
   * Any parent re-render that produces a fresh array reference — a 60s poll
   * tick, an SSE `sms_received`, a lazy query resolving, `setSearchParams`
   * writing `?section=` — would otherwise re-run transition logic mid-flight.
   * The parent memoizes `order` as well; this ref is the guarantee.
   */
  const orderRef = useRef(order);
  orderRef.current = order;

  /** slot index -> the section key it holds, or null when empty */
  const [slots, setSlots] = useState(() => [activeKey, null]);
  /** which slot is live */
  const [front, setFront] = useState(0);
  /** which slot is exiting, or null */
  const [leavingSlot, setLeavingSlot] = useState(null);
  /*
   * Which way the LIVE panel came in, or null on first mount so the console
   * does not slide a panel in before the user has asked for anything. The
   * exit direction is derived from it rather than stored separately — two
   * pieces of state for one fact is how they drift.
   */
  const [enterFrom, setEnterFrom] = useState(null);
  const direction = enterFrom || "right";
  /*
   * Bumped DURING RENDER, before the commit. The unmount timer captures it and
   * bails if it has moved on.
   *
   * Without this: passive-effect cleanups are flushed asynchronously, so
   * between the commit for transition N+1 and the cleanup of transition N's
   * effect there is a task boundary. A timer whose deadline lands in that
   * window still runs — and by then its slot is the one N+1 just made FRONT.
   * It would null the live panel, leaving only a panel mid-exit that settles
   * at opacity 0 and never recovers, because `renderedKey === activeKey` means
   * the transition block never fires again. A permanently blank console.
   * Narrow window, unrecoverable outcome, and the same shape as the strand bug
   * two rewrites ago.
   */
  const txRef = useRef(0);
  const [renderedKey, setRenderedKey] = useState(activeKey);
  /* Bumped once per transition, so the unmount timer re-arms even when the
     same slot index leaves twice in a row. */
  const [transitionId, setTransitionId] = useState(0);

  /*
   * Adjusted DURING RENDER, not in an effect. React's documented escape hatch
   * for deriving state from props: it discards this render and immediately
   * re-renders, so the first commit for a new key is already the entering
   * state. In an effect this runs AFTER paint, which committed the new panel
   * at rest for a frame first — and whether you saw an animation came down to
   * whether the browser happened to paint that frame. It must stay here.
   */
  if (renderedKey !== activeKey) {
    const keys = orderRef.current;
    const goingUp = keys.indexOf(activeKey) > keys.indexOf(renderedKey);
    const nextFront = 1 - front;

    setEnterFrom(goingUp ? "right" : "left");
    txRef.current += 1;
    setSlots((prev) => {
      const next = prev.slice();
      next[nextFront] = activeKey;
      return next;
    });
    setLeavingSlot(front);
    setFront(nextFront);
    setRenderedKey(activeKey);
    setTransitionId((n) => n + 1);
  }

  /* Unmount the outgoing panel once its exit animation has finished. */
  useEffect(() => {
    if (leavingSlot === null) return undefined;
    const slot = leavingSlot;
    const tx = txRef.current;
    const timer = setTimeout(() => {
      // A newer transition has started; this slot is no longer the one leaving.
      if (txRef.current !== tx) return;
      setSlots((prev) => {
        if (prev[slot] === null) return prev;
        const next = prev.slice();
        next[slot] = null;
        return next;
      });
      setLeavingSlot(null);
    }, SLIDE_MS + 40);
    return () => clearTimeout(timer);
    // transitionId, so a second switch restarts the clock rather than letting
    // the first one's timer cut the second exit short.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionId]);

  const currentClass = [
    "scc-vs-panel",
    "is-current",
    enterFrom ? `is-entering-${enterFrom}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const leavingClass = [
    "scc-vs-panel",
    "is-leaving",
    direction === "right" ? "is-leaving-left" : "is-leaving-right",
  ].join(" ");

  return (
    <div className="scc-vs" aria-live="polite">
      {slots.map((key, index) => {
        if (key === null) return null;
        const isFront = index === front;
        return (
          /* Keyed by SLOT, not by section key — that is what keeps a panel in
             one place across a transition and stops it remounting. */
          <div
            key={index}
            className={isFront ? currentClass : leavingClass}
            aria-hidden={isFront ? undefined : "true"}
          >
            {/*
             * The WRAPPER is keyed by slot so a leaving panel is never
             * relocated. Its CONTENTS are keyed by SECTION, so when a slot is
             * recycled — leaving with A, then front with C — React mounts C
             * clean instead of reconciling it in place over A. Without this,
             * two sections that happen to render the same component type would
             * silently share one instance, and its state (scroll position, a
             * half-typed draft, which rows are open) would bleed across.
             */}
            <React.Fragment key={key}>{renderPanel(key)}</React.Fragment>
          </div>
        );
      })}
    </div>
  );
};

export default ClientConsoleVS;
